import { CommonModule, NgClass } from "@angular/common";
import {
    ChangeDetectorRef,
    ChangeDetectionStrategy,
    OnInit,
    OnDestroy,
    Component,
    signal,
    effect,
    ViewEncapsulation,
    inject,
    computed,
    input,
    viewChild,
    ElementRef,
    HostListener,
    output,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { CopyrightModalComponent } from "../../../standalone/modals/copyright.modal.component";
import { RecommendedBrowsersModalComponent } from "../../../standalone/modals/recommended.browsers.modal.component";
import { ViewerMessageModalComponent } from "../../../standalone/modals/viewer.message.modal.component";
import { WelcomeMessageModalComponent } from "../../../standalone/modals/welcome.message.modal.component";
import { S25Util } from "../../../util/s25-util";
import { UserprefService } from "../../../services/userpref.service";
import { ViewerMessageService } from "../../../services/viewer.message.service";
import { AdmitModule } from "../../admit/admit.module";
import { S25ClickOutsideDirective } from "../../s25-click-outside/s25.click.outside.directive";
import { S25HelpDirective } from "../../s25-help/s25.help.directive";
import { S25Help } from "../../s25-help/s25.help.service";
import { S25Const } from "../../../util/s25-const";
import { S25States } from "../../../services/state/state.map";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25MasqueradeModule } from "../../S25-masquerade/masquerade.module";
import { PopoverModule } from "../../s25-popover/popover.module";
import { S25ViewerMessageComponent } from "../../s25-viewer-message/s25.viewer.message.component";
import { S25RouterLinkDirective } from "../s25.router.link.directive";
import { MoreMenu } from "./more.menu";
import MenuSection = MoreMenu.MenuSection;
import { Preference } from "../../../pojo/PeferenceI";
import prefData = Preference.prefData;
import { AppPathsService } from "../../../services/app.paths.service";
import MenuItem = MoreMenu.MenuItem;
import { MoreMenuUtil } from "./more.menu.util";
import { CordovaService } from "../../../services/cordova.service";
import { Debounce } from "../../../decorators/debounce.decorator";
import { PermissionService } from "../../../services/permission.service";
import { LoginStore } from "../../../stores/login.store";
import { FlsStore } from "../../../stores/fls.store";
import { S25MoreMenuItemComponent } from "./s25.more.menu.item.component";

/** Page chrome that must not receive focus while the More menu panel is open. */
const INERT_WHEN_MENU_OPEN_SELECTORS = [
    "a.skip-main",
    "#navbar-container .c-navLogo",
    "#navbar-container .navbar-nav",
    "#mainContent",
    "s25-ng-viewer-message",
];

@TypeManagerDecorator("s25-ng-more-menu")
@Component({
    selector: "s25-ng-more-menu",
    template: `
        <button
            #menuTrigger
            class="c-button c-button--flat c-navToggle"
            (click)="toggleMenu($event)"
            type="button"
            id="more-nav-button"
            aria-haspopup="dialog"
            aria-controls="more-nav-content"
            [attr.aria-expanded]="isNavMenuOpen()"
        >
            <s25-ng-icon [type]="'menu'" [ariaLabel]="false"></s25-ng-icon>
            <span class="c-nav-more">More</span>
        </button>

        @if (isNavMenuOpen()) {
            <div
                s25-ng-click-outside
                (clickOutside)="closeMenu()"
                class="c-navbarExtra"
                role="dialog"
                aria-modal="true"
                aria-label="More menu"
                id="more-nav-content"
                data-testid="moreMenuNav"
            >
                <button class="c-button c-button--flat c-button--close" (click)="closeMenu()" type="button">
                    <s25-ng-icon [type]="'close'" [ariaLabel]="false"></s25-ng-icon>
                    <span class="visually-hidden">Close menu</span>
                </button>
                <nav role="navigation" aria-label="More menu">
                    @if (!StateMap.dash.isHidden && has25LiveAccess) {
                        <a s25-ng-router-link [stateName]="StateNames.dash" class="c-margin-top--half c-nav-home">
                            <s25-ng-icon [type]="'schedulingAdvanced'" [ariaLabel]="false"></s25-ng-icon>
                            Home: {{ StateMap.dash.display }}
                        </a>
                    }
                    @if (!StateMap.rose.isHidden && has25LiveAccess) {
                        <a s25-ng-router-link [stateName]="StateNames.rose" class="c-nav-event">
                            <s25-ng-icon [type]="'navEdit'" [ariaLabel]="false"></s25-ng-icon>
                            Event Form
                        </a>
                    }
                    @if (hasTaskPermissions() && has25LiveAccess) {
                        <a
                            s25-ng-router-link
                            [stateName]="StateNames.search"
                            [stateParams]="{
                                viz: 'list',
                                subject: 'task',
                                search: taskDefault(),
                            }"
                            [options]="{ source: 'state-service' }"
                            class="c-mobileNav--show c-nav-tasks task-notifications"
                            (click)="closeMenu()"
                        >
                            <s25-ng-icon [type]="'task'" [ariaLabel]="false"></s25-ng-icon>
                            <span class="text">Tasks</span>
                            @if (tasksCount()) {
                                <span class="count" [ngClass]="{ 'count--small': +tasksCount() < 9 }">
                                    {{ tasksCount() }}
                                </span>
                            }
                        </a>
                    }
                    @if (hasChatPermissions()) {
                        <a
                            href="javascript:void(0)"
                            (click)="onChatClick($event)"
                            class="c-mobileNav--show c-nav-chat"
                            [attr.aria-label]="hasUnreadChats() ? 'Chat, unread' : null"
                        >
                            <s25-ng-icon [type]="'chat'" class="chat-icon" [ariaLabel]="false"></s25-ng-icon>
                            <span class="text">Chat</span>
                            @if (hasUnreadChats()) {
                                <span class="unread-dot" aria-hidden="true"></span>
                            }
                        </a>
                    }

                    @if (inApp) {
                        <a
                            href="javascript:void(0)"
                            (click)="switchInstance()"
                            class="c-mobileNav--show"
                            id="switch-instance"
                        >
                            <s25-ng-icon [type]="'signout'" [ariaLabel]="false"></s25-ng-icon>
                            Switch Instance
                        </a>
                    }

                    @for (section of sections; track section.name) {
                        @if (section.items.length > 0) {
                            <div class="navTab c-nav--top-border ">
                                <button
                                    type="button"
                                    class="c-tab-label"
                                    [attr.aria-expanded]="section.expanded"
                                    [attr.aria-controls]="'menu-section-' + section.name"
                                    (click)="menuTabToggle(section)"
                                >
                                    {{ section.header }}
                                </button>
                                <div
                                    class="c-menu-content"
                                    [id]="'menu-section-' + section.name"
                                    [attr.data-expanded]="section.expanded"
                                >
                                    @if (section.expanded) {
                                        <ul class="c-menu-list">
                                            @for (item of section.items; track item.displayName) {
                                                <li>
                                                    @if (item.displayName === "What’s New") {
                                                        <a
                                                            class="c-whatsNew"
                                                            [href]="item.url"
                                                            s25Help
                                                            [helpTopic]="'whats_new'"
                                                            >{{ item.displayName }}</a
                                                        >
                                                    } @else {
                                                        <s25-ng-more-menu-item
                                                            [item]="item"
                                                            (itemClicked)="closeMenu()"
                                                        ></s25-ng-more-menu-item>
                                                    }
                                                </li>
                                            }
                                        </ul>
                                        @if (section.name === "about" && inApp && appVersion) {
                                            <span class="c-menu-version">Version {{ appVersion }}</span>
                                        }
                                    }
                                </div>
                            </div>
                        }
                    }
                </nav>

                <div class="navTab c-nav--top-border">
                    <p class="c-tab-label c-menu-username">{{ styledName() }}</p>

                    @if (isLoggedIn) {
                        @if (!disableSignOut) {
                            <a
                                href="javascript:void(0)"
                                (click)="signOut($event)"
                                class="ngAnchor c-signOut"
                                data-nav-link="signOut"
                            >
                                <s25-ng-icon [type]="'signout'" [ariaLabel]="false"></s25-ng-icon>
                                Sign Out
                            </a>
                        }
                    } @else {
                        <a href="javascript:void(0)" class="ngAnchor" (click)="logIn.emit()">Sign In</a>
                    }
                </div>
            </div>
        }
    `,
    styleUrls: ["s25.more.menu.component.scss"],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineModule,
        PopoverModule,
        FormsModule,
        S25IconComponent,
        S25RouterLinkDirective,
        S25MasqueradeModule,
        AdmitModule,
        S25ClickOutsideDirective,
        S25MoreMenuItemComponent,
        S25HelpDirective,
        NgClass,
    ],
})
export class S25MoreMenuComponent implements OnInit, OnDestroy {
    readonly styledName = input("");
    readonly tasksCount = input("");
    readonly taskDefault = input("");
    readonly hasUnreadChats = input(false);

    readonly logOut = output<{
        stateName?: S25States.Name;
        keepRecentlyViewed?: boolean;
        keepJwt?: boolean;
    }>();
    readonly logIn = output<void>();
    readonly chatClick = output<void>();

    private readonly loginStore = inject(LoginStore);
    private readonly fls = inject(FlsStore);

    readonly hasTaskPermissions = computed(
        () => this.loginStore.loggedIn() && this.fls.accessLevel("TASK_LIST") !== "N",
    );
    readonly hasChatPermissions = computed(() => {
        const groupId = this.loginStore.user()?.securityGroupId;
        return this.loginStore.loggedIn() && S25Const.socEnabled && (groupId === -1 || this.fls.hasRead("CHAT"));
    });

    inApp: boolean = S25Const.inApp;
    appVersion: string | undefined = S25Const.appVersion;
    prefs: prefData;
    isLoggedIn: boolean = false;
    disableSignOut: boolean = false;
    has25LiveAccess: boolean = true; // Default to true, will be updated in ngOnInit
    isNavMenuOpen = signal(false);

    private readonly menuTrigger = viewChild<ElementRef<HTMLButtonElement>>("menuTrigger");
    private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>("closeButton");

    constructor(
        private cd: ChangeDetectorRef,
        private hostRef: ElementRef<HTMLElement>,
    ) {
        effect(() => {
            this.setPageChromeInert(this.isNavMenuOpen());
        });
    }

    sections: MenuSection[] = [
        {
            header: "Navigate to...",
            name: "navigateTo",
            expanded: true,
            items: [],
        },
        {
            header: "Go to Tool",
            name: "goToTool",
            expanded: false,
            items: [],
        },
        {
            header: "About",
            name: "about",
            expanded: false,
            items: [],
        },
        {
            header: "Switch Roles",
            name: "switchRoles",
            expanded: false,
            items: [],
        },
    ];

    signOut(event: MouseEvent) {
        event.stopPropagation(); // Prevent the click event from propagating
        this.logOut.emit({ stateName: null, keepRecentlyViewed: false, keepJwt: true });
    }

    /*
    this function will init by just showing the three menu bars
    In the background, it will fetch everything needed to fill the more menu.
    on click of that menu, it will show the menu items
     */
    async ngOnInit() {
        this.prefs = await MoreMenuUtil.getPreferences();
        this.isLoggedIn = await UserprefService.getLoggedIn();
        this.disableSignOut = window.disableSignOut;
        this.has25LiveAccess = await PermissionService.has25LiveAccess();

        for (const section of this.sections) {
            switch (section.name) {
                case "navigateTo":
                    section.items = await MoreMenuUtil.getNavigateTo(this.has25LiveAccess);
                    break;

                case "about":
                    section.items = await this.getAboutLinks();
                    break;

                case "goToTool":
                    if (this.isLoggedIn) {
                        section.items = await this.getExternalLinks();
                    }
                    break;

                case "switchRoles":
                    section.items = await MoreMenuUtil.getSwitchRolesMenuItems();
                    break;
            }
        }

        this.cd.detectChanges();
    }

    ngOnDestroy() {
        this.setPageChromeInert(false);
    }

    async getAboutLinks(): Promise<MenuItem[]> {
        const emailPref = `${this.prefs?.config_feedback_email?.value}` || "";

        const hasViewerMessage = await ViewerMessageService.hasViewerMessageWritePerms().catch(() => false);
        const retVal: MenuItem[] = [
            {
                displayName: "Welcome Message",
                modal: WelcomeMessageModalComponent,
            },
            {
                displayName: "Edit Viewer Message",
                modal: ViewerMessageModalComponent,
                isHidden: !hasViewerMessage,
            },
            {
                displayName: "Contact Admin",
                url: S25Util.eMailLink(emailPref),
            },
            {
                displayName: "Product Feedback",
                state: S25States.typed.productFeedback,
                isHidden: !this.isLoggedIn,
            },
            {
                displayName: "User Community Forums",
                url: "https://www.series25.community/",
            },
            {
                displayName: "What’s New",
                url: S25Help.getHelpUrl("whats_new"),
            },
            {
                displayName: "Accessibility",
                url: "https://collegenet.com/accessibility",
            },
            {
                displayName: "Copyright",
                modal: CopyrightModalComponent,
            },
            {
                displayName: "Privacy Policy",
                url: "https://collegenet.com/privacy",
            },
            {
                displayName: "Browser Recommendations",
                modal: RecommendedBrowsersModalComponent,
                isHidden: S25Const.inApp,
            },
        ];

        return retVal.filter((item) => !item.isHidden || S25Util.isUndefined(item.isHidden));
    }

    async getExternalLinks(): Promise<MenuItem[]> {
        return AppPathsService.getGoToToolMenuItems();
    }

    async switchInstance() {
        await CordovaService.removeSecureStorage("S25InstanceId");
        // switch instance but do not destroy their jwt for re-auth later

        return this.logOut.emit({ stateName: null, keepRecentlyViewed: false, keepJwt: true });
    }

    closeMenu(options?: { restoreFocus?: boolean }) {
        if (!this.isNavMenuOpen()) {
            return;
        }
        this.isNavMenuOpen.set(false);
        this.cd.detectChanges();
        //ANG-7637 closing because user tabbed off the end of the menu
        if (options?.restoreFocus !== false) {
            this.menuTrigger()?.nativeElement.focus();
        }
    }

    onChatClick(event: Event) {
        event.preventDefault();
        this.closeMenu();
        this.chatClick.emit();
    }

    @Debounce(100)
    toggleMenu(event?: MouseEvent) {
        event?.stopPropagation();
        const wasOpen = this.isNavMenuOpen();
        this.isNavMenuOpen.update((prev) => !prev);
        if (!wasOpen) {
            this.cd.detectChanges();
            setTimeout(() => this.closeButton()?.nativeElement.focus());
        } else {
            this.menuTrigger()?.nativeElement.focus();
        }
        this.cd.detectChanges();
    }

    @HostListener("document:keydown.escape")
    onEscape() {
        if (this.isNavMenuOpen()) {
            this.closeMenu();
        }
    }

    @HostListener("focusout", ["$event"])
    onFocusOut(event: FocusEvent) {
        if (!this.isNavMenuOpen()) {
            return;
        }
        const relatedTarget = event.relatedTarget as Node | null;
        if (!relatedTarget) {
            return;
        }
        if (this.hostRef.nativeElement.contains(relatedTarget)) {
            return;
        }
        this.closeMenu({ restoreFocus: false });
    }

    menuTabToggle(section: MenuSection) {
        section.expanded = !section.expanded;
        this.cd.detectChanges();
    }

    private setPageChromeInert(inert: boolean) {
        for (const selector of INERT_WHEN_MENU_OPEN_SELECTORS) {
            document.querySelectorAll(selector).forEach((element) => {
                if (inert) {
                    element.setAttribute("inert", "");
                } else {
                    element.removeAttribute("inert");
                }
            });
        }
    }

    protected readonly StateMap = S25States.untyped;
    protected readonly StateNames = S25States.names;
    protected readonly Object = Object;
}
